import { useState } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/cn";

interface HeatMapCell {
  id: string;
  label: string;
  value: number;
}

interface HeatMapProps {
  cells: HeatMapCell[];
  max?: number;
  columns?: number;
  cellSize?: number;
  gap?: number;
  lowColor?: string;
  highColor?: string;
  onCellClick?: (cell: HeatMapCell) => void;
  format?: (cell: HeatMapCell) => string;
  className?: string;
}

function interpolateOpacity(value: number, max: number): number {
  if (max <= 0) return 0.05;
  return 0.05 + (value / max) * 0.9;
}

function HeatMap({
  cells,
  max: maxProp,
  columns = 4,
  cellSize = 48,
  gap = 4,
  highColor = "var(--color-lever-orange)",
  onCellClick,
  format = (c) => `${c.label}: ${c.value}`,
  className,
}: HeatMapProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const max = maxProp ?? Math.max(...cells.map((c) => c.value), 1);

  return (
    <div
      className={cn("inline-grid", className)}
      style={{
        gridTemplateColumns: `repeat(${columns}, ${cellSize}px)`,
        gap: `${gap}px`,
      }}
      role="grid"
    >
      {cells.map((cell) => {
        const opacity = interpolateOpacity(cell.value, max);
        const isHovered = hovered === cell.id;

        return (
          <motion.button
            key={cell.id}
            role="gridcell"
            aria-label={format(cell)}
            onMouseEnter={() => setHovered(cell.id)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onCellClick?.(cell)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className={cn(
              "relative flex items-center justify-center rounded-[var(--radius-sm)]",
              "text-[10px] font-medium transition-colors",
              "cursor-pointer select-none",
              onCellClick ? "hover:ring-1 hover:ring-[var(--border-default)]" : "",
            )}
            style={{
              width: cellSize,
              height: cellSize,
              backgroundColor: highColor,
              opacity,
            }}
          >
            <span
              className="truncate px-1"
              style={{ color: opacity > 0.5 ? "white" : "var(--text-secondary)" }}
            >
              {cell.label}
            </span>

            {isHovered && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute -top-8 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-[var(--radius-sm)] bg-[var(--bg-surface-solid)] px-2 py-1 text-[10px] font-medium text-[var(--text-primary)] shadow-[var(--shadow-md)] border border-[var(--border-hairline)]"
              >
                {format(cell)}
              </motion.div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

HeatMap.displayName = "HeatMap";

export { HeatMap };
export type { HeatMapProps, HeatMapCell };
